/**
 * Public safety page (Phase C). Opened by family/friends via a share link —
 * no Telegram session required. Shows a minimal, no-PII trip view.
 */
import { useQuery } from '@tanstack/react-query';
import { getSharedTrip } from '../lib/api';
import { jsLocale, useT } from '../lib/i18n';
import { BrandMark } from '../components/Brand';
import { Card, CardHint, CardTitle } from '../components/Card';

function statusKey(status: string): string {
  if (status === 'departed') return 'shared.status.onway';
  if (status === 'completed' || status === 'auto_completed') return 'shared.status.completed';
  if (status === 'cancelled_by_driver' || status === 'rejected') return 'shared.status.cancelled';
  return 'shared.status.scheduled';
}

export function SharedTripPage({ token }: { token: string }): JSX.Element {
  const { t, locale } = useT();
  const q = useQuery({ queryKey: ['shared', token], queryFn: () => getSharedTrip(token), retry: false });

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col px-4 py-8">
      <div className="mb-6 flex items-center gap-2">
        <BrandMark size={32} />
        <span className="text-lg font-bold text-tg-text">Waylo</span>
      </div>

      {q.isLoading && <div className="h-40 animate-pulse rounded-card bg-tg-bg-secondary" />}

      {q.isError && (
        <Card className="text-center">
          <CardTitle>{t('shared.unavailable.t')}</CardTitle>
          <CardHint>{t('shared.unavailable.b')}</CardHint>
        </Card>
      )}

      {q.data && (
        <div className="space-y-3">
          <h1 className="text-xl font-bold text-tg-text">
            {t('shared.subtitle', { name: q.data.passengerFirstName })}
          </h1>

          <Card className="space-y-2">
            <div className="flex items-center justify-between">
              <CardTitle>
                {q.data.originName} → {q.data.destinationName}
              </CardTitle>
              <span
                className={
                  'rounded-pill px-2 py-0.5 text-xs font-semibold ' +
                  (q.data.status === 'departed'
                    ? 'bg-brand-green/15 text-brand-green'
                    : 'bg-tg-bg-secondary text-tg-hint')
                }
              >
                {t(statusKey(q.data.status) as 'shared.status.scheduled')}
              </span>
            </div>
            <CardHint>
              {t('shared.departure')}:{' '}
              {new Date(q.data.departureAt).toLocaleString(jsLocale(locale), {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })}
            </CardHint>
            <CardHint>
              {t('shared.driver')}: {q.data.driverFirstName}
              {q.data.driverConfirmed && ` · ✓ ${t('confirm.driver.done')}`}
            </CardHint>
            {q.data.pickupTitle && (
              <CardHint>
                {t('pickup.cardTitle')}: {q.data.pickupTitle}
              </CardHint>
            )}
          </Card>

          <p className="px-2 text-center text-xs text-tg-hint">{t('shared.footer')}</p>
        </div>
      )}
    </main>
  );
}
